import { db } from "../../../config/firebaseConfig";
import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import {
  uploadToS3,
  downloadFromS3,
  packageCodeToZip,
  extractCodeFromZip,
} from "./s3StorageService";

export const COLLECTION_NAME = "code_space_backpack";
export const MAX_PROJECT_SIZE_BYTES = 100 * 1024; // Strict 100 KB limit for all projects
export const MAX_RECENT_PROJECTS = 50;

export const getProjectsDocId = (studentId, shard = 1) =>
  shard > 1 ? `project_${studentId}_${shard}` : `project_${studentId}`;

/**
 * Format bytes into human-readable string (B, KB, MB).
 */
export const formatBytes = (bytes) => {
  if (!bytes || bytes <= 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Calculate the exact UTF-8 byte size of a project payload.
 */
export const calculateProjectPayloadSize = (payload) => {
  try {
    return new Blob([JSON.stringify(payload)]).size;
  } catch (e) {
    return 0;
  }
};

/**
 * Calculate the exact binary byte size of a project payload (Scratch .sb3, Python/JS .zip, or JSON).
 */
export const getPayloadByteSize = (code, files, isScratchOrJunior) => {
  try {
    if (isScratchOrJunior) {
      const sb3Data =
        (code && code.sb3Data) ||
        (code instanceof ArrayBuffer || code instanceof Blob ? code : null);
      if (sb3Data) {
        if (typeof sb3Data === "string") {
          if (sb3Data.startsWith("data:")) {
            const base64Index = sb3Data.indexOf("base64,");
            const base64Str =
              base64Index !== -1 ? sb3Data.slice(base64Index + 7) : sb3Data;
            const padding = base64Str.endsWith("==")
              ? 2
              : base64Str.endsWith("=")
              ? 1
              : 0;
            return Math.floor((base64Str.length * 3) / 4) - padding;
          }
          return new Blob([sb3Data]).size;
        }
        if (sb3Data instanceof Blob) return sb3Data.size;
        if (sb3Data instanceof ArrayBuffer) return sb3Data.byteLength;
        if (ArrayBuffer.isView(sb3Data)) return sb3Data.byteLength;
      }
      const jsonPayload = (code && code.projectJson) || code || {};
      return new Blob([JSON.stringify(jsonPayload)]).size;
    }
    if (files && Array.isArray(files) && files.length > 0) {
      return files.reduce(
        (acc, f) => acc + (f.content ? new Blob([f.content]).size : 0),
        0
      );
    }
    if (typeof code === "string") {
      return new Blob([code]).size;
    }
    return new Blob([JSON.stringify(code || {})]).size;
  } catch (e) {
    return 0;
  }
};

/**
 * Save a project metadata to the unified code_space_backpack collection.
 * Saves clean metadata into code_space_backpack/{childId}_projects.items array.
 * Heavy file payloads (.sb3, .py, .js) reside in cloud storage under filePath.
 */
export const saveProject = async ({
  projectId,
  childId,
  parentUid,
  projectName,
  environment = "scratch",
  code,
  files,
  visibility = "private",
  thumbnailPath = null,
  entryPoint = null,
}) => {
  try {
    if (!childId) {
      throw new Error("childId is required to save project");
    }

    const targetId = projectId || (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `proj_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`);
    const now = Date.now();

    // Determine target cloud file extension and storage path
    const isScratchOrJunior =
      environment === "scratch" ||
      environment === "junior_blocks_basic" ||
      environment === "junior_blocks_adv" ||
      environment === "scratch_jr" ||
      environment === "junior";

    // Determine target cloud file extension and storage path
    // Scratch projects and Junior blocks both use standard .sb3, Python and JS projects use unified .zip
    const resolveProjectFileType = (env) => {
      if (
        env === "scratch" ||
        env === "junior_blocks_basic" ||
        env === "junior_blocks_adv" ||
        env === "scratch_jr" ||
        env === "junior"
      ) {
        return "sb3";
      }
      return "zip";
    };

    const fileType = resolveProjectFileType(environment);
    const filePath = `projects/${childId}/${targetId}.${fileType}`;

    // Target cloud path for stage thumbnail preview (Scratch and Junior projects)
    const resolvedThumbnailPath =
      thumbnailPath ||
      (isScratchOrJunior
        ? `projects/${childId}/${targetId}_thumb.webp`
        : null);

    // Multi-file and entry point metadata for Python / JS projects
    const hasFilesArray = Array.isArray(files) && files.length > 0;
    const fileCount = hasFilesArray
      ? files.filter((f) => f.type !== "folder").length
      : 1;
    const isMultiFile = isScratchOrJunior ? false : (hasFilesArray && fileCount > 1);

    const resolvedEntryPoint =
      entryPoint ||
      (environment === "python"
        ? "main.py"
        : environment === "javascript"
        ? "main.js"
        : null);

    // Exact binary payload size calculation
    let payloadBytes = 0;
    let preparedZipBlob = null;

    if (isScratchOrJunior) {
      payloadBytes = getPayloadByteSize(code, files, true);
    } else if (environment === "python" || environment === "javascript") {
      preparedZipBlob = await packageCodeToZip({
        code: typeof code === "string" ? code : "",
        files,
        environment,
        entryPoint: resolvedEntryPoint,
      });
      payloadBytes = preparedZipBlob.size;
    } else {
      payloadBytes = getPayloadByteSize(code, files, false);
    }

    // STRICT 100 KB LIMIT: Block cloud storage uploads and DB commits if size exceeds limit
    if (payloadBytes > MAX_PROJECT_SIZE_BYTES) {
      const error = new Error(
        `Project size (${formatBytes(payloadBytes)}) exceeds the maximum allowed limit of ${formatBytes(MAX_PROJECT_SIZE_BYTES)}. Please remove unused assets, sounds, or sprites to save.`
      );
      error.code = "PROJECT_SIZE_EXCEEDED";
      error.actualSize = payloadBytes;
      error.maxSize = MAX_PROJECT_SIZE_BYTES;
      throw error;
    }

    // Upload project payload and thumbnail directly to AWS S3
    if (isScratchOrJunior) {
      const sb3Data = (code && code.sb3Data) || (code instanceof ArrayBuffer || code instanceof Blob ? code : null);
      if (sb3Data) {
        await uploadToS3(filePath, sb3Data, "application/x.scratch.sb3");
      } else {
        const jsonPayload = (code && code.projectJson) || code || {};
        await uploadToS3(filePath, JSON.stringify(jsonPayload, null, 2), "application/json");
      }

      // Upload stage thumbnail if available
      const stageThumb = (code && code.thumbnail) || null;
      if (stageThumb && resolvedThumbnailPath) {
        try {
          await uploadToS3(resolvedThumbnailPath, stageThumb, "image/webp");
        } catch (thumbErr) {
          console.warn("Failed to upload project thumbnail to S3:", thumbErr);
        }
      }
    } else if (environment === "python" || environment === "javascript") {
      const zipBlob =
        preparedZipBlob ||
        (await packageCodeToZip({
          code: typeof code === "string" ? code : "",
          files,
          environment,
          entryPoint: resolvedEntryPoint,
        }));
      await uploadToS3(filePath, zipBlob, "application/zip");
    }

    // Clean project metadata item (No raw code or files array bloat in Firestore!)
    const projectItem = {
      id: targetId,
      projectName: projectName || "Unnamed Project",
      environment,
      fileType,
      filePath,
      entryPoint: resolvedEntryPoint,
      isMultiFile,
      fileCount: isScratchOrJunior ? 1 : fileCount,
      thumbnailPath: resolvedThumbnailPath,
      visibility: visibility || "private",
      sizeBytes: payloadBytes,
      createdAt: now,
      updatedAt: now,
    };

    const docRef = doc(db, COLLECTION_NAME, getProjectsDocId(childId));

    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(docRef);
      let items = [];
      let createdAt = now;

      if (snap.exists()) {
        items = snap.data().items || [];
      }

      const existingIndex = items.findIndex((item) => item.id === targetId);
      if (existingIndex >= 0) {
        createdAt = items[existingIndex].createdAt || now;
        projectItem.createdAt = createdAt;
        items[existingIndex] = { ...items[existingIndex], ...projectItem };
      } else {
        projectItem.createdAt = now;
        items.unshift(projectItem);
        // Cap active document to 50 recent projects
        if (items.length > MAX_RECENT_PROJECTS) {
          items = items.slice(0, MAX_RECENT_PROJECTS);
        }
      }

      transaction.set(
        docRef,
        {
          type: "projects",
          childId,
          parentUid: parentUid || null,
          updatedAt: serverTimestamp(),
          items,
        },
        { merge: true }
      );
    });

    return projectItem;
  } catch (error) {
    console.error("Error saving project metadata to code_space_backpack:", error);
    throw error;
  }
};

/**
 * Fetch all saved projects for a specific child from code_space_backpack/{childId}_projects.
 * Executes a single point lookup (1 read).
 */
export const getSavedProjects = async (childId, environment = null) => {
  try {
    if (!childId) return [];

    const primaryDocRef = doc(db, COLLECTION_NAME, getProjectsDocId(childId, 1));
    const snap = await getDoc(primaryDocRef);

    let items = [];
    if (snap.exists()) {
      items = snap.data().items || [];
    }

    // Check if secondary shard exists (project_{childId}_2)
    try {
      const secondaryDocRef = doc(db, COLLECTION_NAME, getProjectsDocId(childId, 2));
      const secSnap = await getDoc(secondaryDocRef);
      if (secSnap.exists()) {
        const secItems = secSnap.data().items || [];
        items = items.concat(secItems);
      }
    } catch (e) {
      // Shard 2 optional
    }

    if (environment) {
      items = items.filter((p) => p.environment === environment);
    }

    // Sort descending by updatedAt
    items.sort((a, b) => {
      const timeA = typeof a.updatedAt === "number" ? a.updatedAt : (a.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0);
      const timeB = typeof b.updatedAt === "number" ? b.updatedAt : (b.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0);
      return timeB - timeA;
    });

    return items;
  } catch (error) {
    console.error("Error fetching projects from code_space_backpack:", error);
    throw error;
  }
};

/**
 * Delete a project by ID from code_space_backpack/project_{childId}.
 */
export const deleteProject = async (projectId, childId) => {
  try {
    if (!projectId) throw new Error("projectId is required");
    if (!childId) throw new Error("childId is required to delete project from code_space_backpack");

    const docRef = doc(db, COLLECTION_NAME, getProjectsDocId(childId, 1));
    let deleted = false;

    await runTransaction(db, async (transaction) => {
      const snap = await transaction.get(docRef);
      if (snap.exists()) {
        const currentItems = snap.data().items || [];
        const updatedItems = currentItems.filter((item) => item.id !== projectId);
        if (updatedItems.length !== currentItems.length) {
          transaction.update(docRef, {
            items: updatedItems,
            updatedAt: serverTimestamp(),
          });
          deleted = true;
        }
      }
    });

    if (!deleted) {
      // Check secondary shard (project_{childId}_2)
      const secDocRef = doc(db, COLLECTION_NAME, getProjectsDocId(childId, 2));
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(secDocRef);
        if (snap.exists()) {
          const currentItems = snap.data().items || [];
          const updatedItems = currentItems.filter((item) => item.id !== projectId);
          if (updatedItems.length !== currentItems.length) {
            transaction.update(secDocRef, {
              items: updatedItems,
              updatedAt: serverTimestamp(),
            });
          }
        }
      });
    }

    return true;
  } catch (error) {
    console.error("Error deleting project from code_space_backpack:", error);
    throw error;
  }
};

/**
 * Load project content (code / files / sb3 buffer) from S3 or metadata fallback.
 */
export const loadProjectData = async (projectItem) => {
  if (!projectItem) return null;

  // Backward compatibility: If code or files are already in the Firestore document
  if (
    projectItem.code ||
    (Array.isArray(projectItem.files) && projectItem.files.length > 0)
  ) {
    return {
      code: projectItem.code || null,
      files: projectItem.files || null,
      environment: projectItem.environment,
    };
  }

  if (!projectItem.filePath) {
    throw new Error("Project has no storage filePath specified.");
  }

  if (
    projectItem.fileType === "sb3" ||
    projectItem.environment === "scratch" ||
    projectItem.environment === "junior_blocks_basic" ||
    projectItem.environment === "junior_blocks_adv" ||
    projectItem.environment === "scratch_jr" ||
    projectItem.environment === "junior"
  ) {
    // Download Scratch/Junior .sb3 as ArrayBuffer
    const buffer = await downloadFromS3(projectItem.filePath, "arrayBuffer");
    return {
      buffer,
      code: null,
      files: null,
      environment: projectItem.environment || "scratch",
    };
  }

  if (
    projectItem.fileType === "zip" ||
    projectItem.environment === "python" ||
    projectItem.environment === "javascript"
  ) {
    const zipBlob = await downloadFromS3(projectItem.filePath, "blob");
    const { files, entryCode } = await extractCodeFromZip(zipBlob, {
      environment: projectItem.environment,
      entryPoint: projectItem.entryPoint,
    });
    return {
      code: entryCode,
      files,
      environment: projectItem.environment,
    };
  }

  const text = await downloadFromS3(projectItem.filePath, "text");
  return {
    code: text,
    files: null,
    environment: projectItem.environment,
  };
};

